import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TeamManagement = ({ teamMembers, onInviteMember, onUpdatePermissions }) => {
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'content-manager',
    permissions: []
  });

  const roleOptions = [
    { value: 'content-manager', label: 'Content Manager' },
    { value: 'editor', label: 'Editor' }
  ];

  const permissionOptions = [
    { value: 'events', label: 'Manage Events' },
    { value: 'venues', label: 'Manage Venues' },
    { value: 'merchandise', label: 'Manage Merchandise' },
    { value: 'reports', label: 'View Reports' }
  ];

  const handleInvite = () => {
    if (inviteData?.email && inviteData?.role) {
      onInviteMember(inviteData);
      setInviteData({ email: '', role: 'content-manager', permissions: [] });
      setShowInviteForm(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      'content-manager': { color: 'bg-primary text-primary-foreground', label: 'Content Manager' },
      'editor': { color: 'bg-secondary text-secondary-foreground', label: 'Editor' },
      'admin': { color: 'bg-accent text-accent-foreground', label: 'Admin' }
    };

    const config = roleConfig?.[role] || roleConfig?.['content-manager'];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-success text-success-foreground', label: 'Active' },
      pending: { color: 'bg-warning text-warning-foreground', label: 'Pending' },
      inactive: { color: 'bg-muted text-muted-foreground', label: 'Inactive' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Team Management</h3>
            <p className="text-sm text-muted-foreground">Manage content managers and permissions</p>
          </div>
          <Button
            variant="default"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setShowInviteForm(true)}
          >
            Invite Member
          </Button>
        </div>
      </div>
      {showInviteForm && (
        <div className="p-6 border-b border-border bg-muted/30">
          <h4 className="text-md font-medium text-foreground mb-4">Invite Team Member</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Email Address"
              type="email"
              placeholder="member@example.com"
              value={inviteData?.email}
              onChange={(e) => setInviteData({ ...inviteData, email: e?.target?.value })}
              required
            />
            <Select
              label="Role"
              options={roleOptions}
              value={inviteData?.role}
              onChange={(value) => setInviteData({ ...inviteData, role: value })}
            />
          </div>
          <div className="mt-4">
            <label className="text-sm font-medium text-foreground mb-2 block">Permissions</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {permissionOptions?.map((permission) => (
                <label key={permission?.value} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-border"
                    checked={inviteData?.permissions?.includes(permission?.value)}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        setInviteData({
                          ...inviteData,
                          permissions: [...inviteData?.permissions, permission?.value]
                        });
                      } else {
                        setInviteData({
                          ...inviteData,
                          permissions: inviteData?.permissions?.filter(p => p !== permission?.value)
                        });
                      }
                    }}
                  />
                  <span className="text-sm text-foreground">{permission?.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowInviteForm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleInvite}
              disabled={!inviteData?.email || !inviteData?.role}
            >
              Send Invite
            </Button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Member</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Role</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Last Active</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Permissions</th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {teamMembers?.map((member) => (
              <tr key={member?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      {member?.avatar ? (
                        <img
                          src={member?.avatar}
                          alt={member?.name}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <Icon name="User" size={20} color="white" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{member?.name}</h4>
                      <p className="text-sm text-muted-foreground">{member?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  {getRoleBadge(member?.role)}
                </td>
                <td className="p-4">
                  {getStatusBadge(member?.status)}
                </td>
                <td className="p-4">
                  <p className="text-sm text-foreground">
                    {member?.lastActive ? new Date(member.lastActive)?.toLocaleDateString() : 'Never'}
                  </p>
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-1">
                    {member?.permissions?.slice(0, 2)?.map((permission) => (
                      <span
                        key={permission}
                        className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs"
                      >
                        {permissionOptions?.find(p => p?.value === permission)?.label}
                      </span>
                    ))}
                    {member?.permissions?.length > 2 && (
                      <span className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs">
                        +{member?.permissions?.length - 2} more
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Settings"
                      onClick={() => onUpdatePermissions(member?.id)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="MoreHorizontal"
                      onClick={() => {}}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {teamMembers?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">No team members</h4>
          <p className="text-muted-foreground mb-4">
            Invite content managers to help manage your events and content
          </p>
          <Button
            variant="default"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setShowInviteForm(true)}
          >
            Invite Member
          </Button>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;